/**
 * UserSettingsModal - 用户个人设置弹窗
 * 包含：修改密码、上传头像（带裁剪）
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, User, Lock, Camera, Loader2, Eye, EyeOff, HardDrive, RefreshCw } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import AvatarCropperModal from './AvatarCropperModal';
import AlertToast from './AlertToast';
import ConfirmModal from './ConfirmModal';
import localCacheService from '../services/localCacheService';
import { sha256Hex } from '../utils/browserCompat'; // http 站点没有 crypto.subtle，去重要能降级

export default function UserSettingsModal({ open, onClose, onAvatarUpdated }) {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('avatar'); // 'avatar' | 'password'
  
  // 头像相关
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || null);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const fileInputRef = useRef(null);
  
  // 🆕 裁剪相关
  const [cropImageUrl, setCropImageUrl] = useState(null); // 待裁剪的原图
  
  // 密码相关
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  
  // 🆕 密码显示/隐藏
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // 🆕 统一 Toast 提示
  const [toast, setToast] = useState({ message: '', type: 'success' });

  // 🆕 本地缓存配置
  const [cachePath, setCachePath] = useState(null);
  const [cacheLoading, setCacheLoading] = useState(false);
  const [showCacheConfirm, setShowCacheConfirm] = useState(false);
  
  // Toast 自动消失
  useEffect(() => {
    if (toast.message) {
      const timer = setTimeout(() => setToast({ message: '', type: 'success' }), 2500);
      return () => clearTimeout(timer);
    }
  }, [toast.message]);
  
  // 🆕 初始化时验证缓存配置（检查文件夹是否真实可用）
  useEffect(() => {
    if (open && localCacheService.isFileSystemAccessSupported()) {
      localCacheService.validateCacheConfig().then(result => {
        if (result.valid) {
          setCachePath(result.path);
        } else {
          setCachePath(null);
        }
      });
    }
  }, [open]);

  // 🆕 监听全局缓存配置变更事件（其他组件触发重配时同步更新）
  useEffect(() => {
    const handleCacheChange = (e) => {
      if (e.detail?.valid) {
        setCachePath(e.detail.path);
      } else {
        setCachePath(null);
      }
    };
    window.addEventListener('cache-config-changed', handleCacheChange);
    return () => window.removeEventListener('cache-config-changed', handleCacheChange);
  }, []);
  
  // 🆕 配置本地缓存
  const handleConfigureCache = () => {
    setShowCacheConfirm(true);
  };
  
  const handleCacheConfirm = async () => {
    setShowCacheConfirm(false);
    setCacheLoading(true);
    const result = await localCacheService.requestCacheFolder();
    setCacheLoading(false);
    
    if (result.success) {
      setCachePath(result.path);
      setToast({ message: '本地缓存已配置', type: 'success' });
    } else if (result.error && result.error !== '已取消选择') {
      setToast({ message: result.error, type: 'error' });
    }
  };

  // 选择头像文件 -> 打开裁剪弹窗
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // 验证文件类型
    if (!file.type.startsWith('image/')) {
      setToast({ message: '请选择图片文件', type: 'warning' });
      return;
    }
    
    // 验证文件大小（最大 30MB，裁剪后会压缩）
    if (file.size > 30 * 1024 * 1024) {
      setToast({ message: '图片大小不能超过 30MB', type: 'warning' });
      return;
    }
    
    // 读取为 DataURL，打开裁剪弹窗
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropImageUrl(e.target.result);
    };
    reader.readAsDataURL(file);
    
    // 清空 input，允许重复选择同一文件
    e.target.value = '';
  };

  // 🆕 裁剪完成后上传
  const handleCropSave = async (croppedBlob) => {
    setCropImageUrl(null); // 关闭裁剪弹窗
    setAvatarLoading(true);
    
    try {
      // 🆕 计算文件 SHA-256 哈希，检查是否已上传过
      // 拿不到哈希（http 非安全上下文没有 crypto.subtle）就跳过去重预检，直接上传
      const buffer = await croppedBlob.arrayBuffer();
      const hashHex = await sha256Hex(buffer);

      let uploadUrl;
      const checkResult = hashHex
        ? await api.get(`/api/upload/check-hash?hash=${hashHex}&type=avatar`)
        : { exists: false };
      if (checkResult.exists && checkResult.url) {
        uploadUrl = checkResult.url;
      } else {
        // 哈希不存在，正常上传
        const formData = new FormData();
        const fileName = `avatar-${Date.now()}.jpg`;
        formData.append('file', croppedBlob, fileName);
        
        const uploadResult = await api.upload('/api/upload?type=avatar', formData);
        
        if (!uploadResult.url) {
          throw new Error('上传失败');
        }
        uploadUrl = uploadResult.url;
      }
      
      // 2. 更新用户头像
      await api.put('/api/auth/avatar', { avatar: uploadUrl });
      
      // 3. 刷新用户信息
      await refreshUser();
      
      // 4. 更新预览
      setAvatarPreview(uploadUrl);
      
      // 5. 通知父组件头像已更新
      if (onAvatarUpdated) {
        onAvatarUpdated();
      }
      
      setToast({ message: '头像更新成功！', type: 'success' });
      
    } catch (err) {
      console.error('保存头像失败:', err);
      setToast({ message: err.message || '保存头像失败', type: 'warning' });
    } finally {
      setAvatarLoading(false);
    }
  };

  // 修改密码
  const handleChangePassword = async () => {
    setPasswordError('');
    
    // 验证
    if (!oldPassword) {
      setPasswordError('请输入旧密码');
      return;
    }
    if (!newPassword) {
      setPasswordError('请输入新密码');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('新密码长度至少 6 个字符');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('两次输入的新密码不一致');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await api.post('/api/auth/change-password', {
        oldPassword,
        newPassword
      });
      
      // 🆕 使用居中 Toast 弹窗提示成功
      setToast({ message: '密码修改成功！', type: 'success' });
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
    } catch (err) {
      setPasswordError(err.message || '修改密码失败');
    } finally {
      setPasswordLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        width: 420, background: 'var(--modal-surface)',
        borderRadius: 16, overflow: 'hidden',
        border: '1px solid var(--border-subtle)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px', borderBottom: '1px solid var(--border-subtle)'
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
            个人设置
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              padding: '8px', cursor: 'pointer', color: 'var(--text-muted)'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', borderBottom: '1px solid var(--border-subtle)'
        }}>
          <button
            onClick={() => setActiveTab('avatar')}
            style={{
              flex: 1, padding: '14px', border: 'none',
              background: activeTab === 'avatar' ? 'rgba(59,130,246,0.1)' : 'transparent',
              color: activeTab === 'avatar' ? '#3b82f6' : 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 14, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              borderBottom: activeTab === 'avatar' ? '2px solid #3b82f6' : '2px solid transparent'
            }}
          >
            <Camera size={16} /> 修改头像
          </button>
          <button
            onClick={() => setActiveTab('password')}
            style={{
              flex: 1, padding: '14px', border: 'none',
              background: activeTab === 'password' ? 'rgba(59,130,246,0.1)' : 'transparent',
              color: activeTab === 'password' ? '#3b82f6' : 'var(--text-secondary)',
              cursor: 'pointer', fontSize: 14, fontWeight: 500,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              borderBottom: activeTab === 'password' ? '2px solid #3b82f6' : '2px solid transparent'
            }}
          >
            <Lock size={16} /> 修改密码
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 24, height: 380, boxSizing: 'border-box' }}>
          {activeTab === 'avatar' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 20 }}>
              {/* 头像预览 */}
              <div style={{
                width: 120, height: 120, borderRadius: '50%',
                background: '#27272a', overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '3px solid var(--border-subtle)',
                position: 'relative'
              }}>
                {avatarPreview && (
                  <img 
                    src={avatarPreview} 
                    alt="avatar" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute' }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                )}
                <User size={48} style={{ color: '#52525b', display: avatarPreview ? 'none' : 'block' }} />
              </div>
              
              {/* 选择文件按钮 */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarLoading}
                style={{
                  background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)',
                  padding: '10px 24px', borderRadius: 8, cursor: 'pointer',
                  color: 'var(--text-body)', fontSize: 14,
                  display: 'flex', alignItems: 'center', gap: 8,
                  opacity: avatarLoading ? 0.6 : 1
                }}
              >
                {avatarLoading ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> 上传中...</>
                ) : (
                  <><Camera size={16} /> 选择图片</>
                )}
              </button>
              
              <p style={{ color: '#71717a', fontSize: 12, margin: 0 }}>
                支持 JPG、PNG 格式，选择后可裁剪，文件不能大于 30MB
              </p>
              
              {/* 🆕 本地缓存配置区域 */}
              {localCacheService.isFileSystemAccessSupported() && (
                <div style={{
                  marginTop: 16,
                  padding: '12px 16px',
                  background: cachePath ? 'rgba(51, 65, 85, 0.5)' : 'rgba(127, 29, 29, 0.3)',
                  border: cachePath ? '1px solid rgba(100, 116, 139, 0.5)' : '1px solid rgba(239, 68, 68, 0.4)',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <HardDrive size={20} color={cachePath ? '#94a3b8' : '#f87171'} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ 
                      fontSize: 12, 
                      color: cachePath ? '#94a3b8' : '#fca5a5', 
                      fontWeight: 'bold',
                      marginBottom: 2
                    }}>
                      本地缓存
                    </div>
                    <div style={{ 
                      fontSize: 11, 
                      color: cachePath ? '#cbd5e1' : '#94a3b8',
                      wordBreak: 'break-all',
                      lineHeight: 1.3
                    }}>
                      {cachePath ? cachePath : '未配置 - 媒体文件每次从服务器加载'}
                    </div>
                  </div>
                  <button
                    onClick={handleConfigureCache}
                    disabled={cacheLoading}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: cachePath ? '1px solid rgba(100, 116, 139, 0.5)' : '1px solid rgba(239, 68, 68, 0.5)',
                      background: cachePath ? 'rgba(71, 85, 105, 0.4)' : 'rgba(239, 68, 68, 0.2)',
                      color: cachePath ? '#cbd5e1' : '#fca5a5',
                      fontSize: 12,
                      fontWeight: 'bold',
                      cursor: cacheLoading ? 'wait' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                      whiteSpace: 'nowrap',
                      transition: 'all 0.2s'
                    }}
                  >
                    {cacheLoading ? (
                      <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                    ) : (
                      <RefreshCw size={12} />
                    )}
                    {cachePath ? '更换' : '配置'}
                  </button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'password' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* 错误提示 */}
              {passwordError && (
                <div style={{
                  padding: '10px 14px', borderRadius: 8,
                  background: 'rgba(239,68,68,0.1)', color: '#f87171',
                  fontSize: 13
                }}>
                  {passwordError}
                </div>
              )}
              
              {/* 旧密码 */}
              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>
                  旧密码
                </label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                  placeholder="请输入旧密码"
                  style={{
                    width: '100%', padding: '12px 14px', borderRadius: 8,
                    background: '#0a0a0a', border: '1px solid var(--border-subtle)',
                    color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box'
                  }}
                />
              </div>
              
              {/* 新密码 */}
              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>
                  新密码
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="请输入新密码（至少6位）"
                    style={{
                      width: '100%', padding: '12px 42px 12px 14px', borderRadius: 8,
                      background: '#0a0a0a', border: '1px solid var(--border-subtle)',
                      color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: '#71717a', padding: 4, display: 'flex', alignItems: 'center'
                    }}
                  >
                    {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              {/* 确认密码 */}
              <div>
                <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, marginBottom: 6 }}>
                  确认新密码
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    placeholder="请再次输入新密码"
                    style={{
                      width: '100%', padding: '12px 42px 12px 14px', borderRadius: 8,
                      background: '#0a0a0a', border: '1px solid var(--border-subtle)',
                      color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{
                      position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: '#71717a', padding: 4, display: 'flex', alignItems: 'center'
                    }}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              {/* 提交按钮 */}
              <button
                onClick={handleChangePassword}
                disabled={passwordLoading}
                style={{
                  background: '#3b82f6', border: 'none',
                  padding: '12px', borderRadius: 8, cursor: 'pointer',
                  color: '#fff', fontSize: 14, fontWeight: 500,
                  marginTop: 8, opacity: passwordLoading ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                {passwordLoading ? (
                  <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> 处理中...</>
                ) : (
                  '确认修改'
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* 🆕 头像裁剪弹窗 */}
      {cropImageUrl && (
        <AvatarCropperModal
          imageUrl={cropImageUrl}
          onClose={() => setCropImageUrl(null)}
          onSave={handleCropSave}
        />
      )}

      {/* 🆕 统一 Toast 提示 */}
      <AlertToast message={toast.message} type={toast.type} />

      {/* 🆕 缓存配置确认弹窗 */}
      {showCacheConfirm && (
        <ConfirmModal
          title="配置缓存位置"
          message="浏览器将请求该文件夹权限，正常安全机制（请牢记文件夹路径，以便在剪辑软件中找到这些素材）。"
          confirmText="开始选择"
          cancelText="取消"
          onConfirm={handleCacheConfirm}
          onCancel={() => setShowCacheConfirm(false)}
        />
      )}
    </div>
  );
}
